import React, { useCallback } from 'react';
import { VehicleTag } from '../../types';
import { TagIcon } from '@heroicons/react/24/outline';

interface VehicleTagSelectorProps {
  value?: VehicleTag[];
  onChange: (tags: VehicleTag[]) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  className?: string;
  error?: string;
}

interface TagOption {
  value: VehicleTag;
  label: string;
  description: string;
  color: string; // Tailwind color class
}

const TAG_OPTIONS: TagOption[] = [
  {
    value: VehicleTag.IMMOBILIZED,
    label: '不動車',
    description: 'エンジンがかからない・走行不可の車両',
    color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  },
  {
    value: VehicleTag.LOW_HEIGHT,
    label: '低車高',
    description: '車高が低く特別な配慮が必要な車両',
    color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  },
  {
    value: VehicleTag.WIDE_BODY,
    label: 'ワイドボディ',
    description: '車幅が広く運搬に注意が必要な車両',
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  },
  {
    value: VehicleTag.WELFARE,
    label: '福祉車両',
    description: '車椅子対応など福祉用途の車両',
    color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  },
  {
    value: VehicleTag.CAMPING,
    label: 'キャンピングカー',
    description: 'キャンピング・レジャー用途の車両',
    color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  },
  {
    value: VehicleTag.CLASSIC,
    label: 'クラシックカー',
    description: '希少価値・歴史的価値のある車両',
    color: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
  },
];

export const VehicleTagSelector: React.FC<VehicleTagSelectorProps> = ({
  value = [],
  onChange,
  placeholder: _placeholder = '車両タグを選択してください',
  label,
  disabled = false,
  className = '',
  error,
}) => {
  const handleTagToggle = useCallback(
    (tag: VehicleTag) => {
      const newTags = value.includes(tag) ? value.filter(t => t !== tag) : [...value, tag];
      onChange(newTags);
    },
    [value, onChange]
  );

  const handleClearAll = () => {
    onChange([]);
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* ラベルとクリアボタン */}
      <div className='flex items-center justify-between'>
        {label && (
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>
            {label}
          </label>
        )}
        {value.length > 0 && (
          <button
            type='button'
            onClick={handleClearAll}
            disabled={disabled}
            className='text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline focus:outline-none'
          >
            すべてクリア
          </button>
        )}
      </div>

      {/* チェックボックスグリッド */}
      <div
        className={`
        grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-4 border rounded-lg
        ${!error ? 'border-gray-300 dark:border-gray-600' : 'border-red-300 dark:border-red-600'}
        ${disabled ? 'bg-gray-50 dark:bg-gray-700' : 'bg-white dark:bg-gray-800'}
      `}
      >
        {TAG_OPTIONS.map(option => {
          const isSelected = value.includes(option.value);
          return (
            <label
              key={option.value}
              className={`
                flex items-start p-3 rounded-lg cursor-pointer transition-colors
                ${disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-gray-50 dark:hover:bg-gray-700'}
                ${isSelected ? 'bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-700' : 'border border-transparent'}
              `}
            >
              <input
                type='checkbox'
                checked={isSelected}
                onChange={() => !disabled && handleTagToggle(option.value)}
                disabled={disabled}
                className='mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded disabled:opacity-50'
              />
              <div className='ml-3 flex-1'>
                <div className='flex items-center mb-1'>
                  <TagIcon className='h-4 w-4 text-gray-400 mr-2' />
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${option.color}`}
                  >
                    {option.label}
                  </span>
                </div>
                <p className='text-xs text-gray-500 dark:text-gray-400'>{option.description}</p>
              </div>
            </label>
          );
        })}
      </div>

      {/* 選択状況の表示 */}
      {value.length > 0 && (
        <div className='text-xs text-gray-500 dark:text-gray-400'>
          {value.length}個のタグを選択中
        </div>
      )}

      {error && <p className='mt-1 text-sm text-red-600 dark:text-red-400'>{error}</p>}
    </div>
  );
};

export default VehicleTagSelector;
